import Cookies from 'js-cookie';
import { getDeviceHeaders } from './deviceSignals';

const JWT_COOKIE = 'c8_jwt';
const SESSION_COOKIE = 'c8_session_token';
const USER_ID_COOKIE = 'cr8_userId';
const USER_CACHE_KEY = 'c8_user_cache';

/**
 * Auth values mirrored into localStorage for durable first-party persistence.
 *
 * Privacy-hardened browsers (Firefox ETP, DuckDuckGo) purge first-party cookies, and Safari ITP
 * caps script-set (document.cookie) cookies to ~7 days — which silently logs users out. localStorage
 * for the site the user is actually visiting is not subject to those policies, so we treat it as the
 * source of truth and keep the cookie as a mirror (many call sites still read the cookie directly).
 */
const DURABLE_AUTH_KEYS = [SESSION_COOKIE, JWT_COOKIE, USER_ID_COOKIE] as const;

export type AuthCookieOptions = {
  expires?: number;
  secure?: boolean;
  sameSite?: 'strict' | 'lax';
};

/**
 * Cookie options for session/JWT tokens.
 *
 * SameSite=Lax (not Strict) is intentional: these cookies are read by JS via document.cookie
 * and the API authenticates with Authorization: Bearer (CORS credentials: false), so Strict
 * provides no security benefit but causes Chrome to drop/evict the cookie more aggressively
 * (and can also cause the cookie to be missing on the initial top-level cross-site navigation,
 * e.g. clicks from Google search, email, or social links). Lax keeps the cookie available on
 * top-level GET navigations while still blocking it on cross-site subresource sends.
 *
 * Safari on HTTPS requires Secure when SameSite is set, so Secure is set automatically on HTTPS.
 */
export function getAuthCookieOptions(overrides?: AuthCookieOptions): AuthCookieOptions {
  const onHttps =
    typeof window !== 'undefined' && window.location.protocol === 'https:';
  return {
    expires: overrides?.expires ?? 365,
    secure: overrides?.secure ?? onHttps,
    sameSite: overrides?.sameSite ?? 'lax',
  };
}

/** localStorage is disabled/unavailable in some privacy modes — probe once and degrade gracefully. */
const LOCAL_STORAGE_AVAILABLE = (() => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const probe = '__c8_ls_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
})();

function lsGet(key: string): string | undefined {
  if (!LOCAL_STORAGE_AVAILABLE) return undefined;
  try {
    const value = window.localStorage.getItem(key);
    return value == null || value === '' ? undefined : value;
  } catch {
    return undefined;
  }
}

function lsSet(key: string, value: string): void {
  if (!LOCAL_STORAGE_AVAILABLE) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore (quota / disabled)
  }
}

function lsRemove(key: string): void {
  if (!LOCAL_STORAGE_AVAILABLE) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * Read a durable auth value: localStorage first (survives cookie purging), cookie as fallback.
 * When only one store has the value, back-fill the other so both stay in sync (this also upgrades
 * already-logged-in users on first load after this build ships).
 */
function readDurable(name: string): string | undefined {
  const fromLs = lsGet(name);
  if (fromLs) {
    if (!Cookies.get(name)) {
      // Keep the cookie mirror alive for the many call sites that read cookies directly.
      Cookies.set(name, fromLs, getAuthCookieOptions());
    }
    return fromLs;
  }
  const fromCookie = Cookies.get(name);
  if (fromCookie) {
    lsSet(name, fromCookie);
    return fromCookie;
  }
  return undefined;
}

/** Write a durable auth value to both localStorage (durable) and cookie (mirror). */
function writeDurable(name: string, value: string, options?: AuthCookieOptions): void {
  lsSet(name, value);
  Cookies.set(name, value, getAuthCookieOptions(options));
}

/** Remove a cookie across attribute variants (legacy secure/http + SameSite combos). */
function removeCookieEverywhere(name: string): void {
  const path = '/';
  const variants: Array<Cookies.CookieAttributes> = [
    { path },
    { path, secure: true },
    { path, secure: false },
    { path, secure: true, sameSite: 'strict' },
    { path, secure: true, sameSite: 'lax' },
    { path, secure: false, sameSite: 'strict' },
    { path, secure: false, sameSite: 'lax' },
  ];
  for (const opts of variants) {
    Cookies.remove(name, opts);
  }
  Cookies.remove(name);
}

/** Remove a durable auth value from both localStorage and cookie. */
function removeDurable(name: string): void {
  lsRemove(name);
  removeCookieEverywhere(name);
}

/**
 * Reconcile cookie <-> localStorage auth on app boot.
 *
 * - Restores the cookie mirror from durable localStorage (privacy browsers purge cookies but keep
 *   localStorage), so the many call sites that read c8_session_token directly keep working.
 * - Back-fills localStorage from an existing cookie (already-logged-in users upgrading to this build).
 *
 * Safe to call multiple times; must run before the first component reads auth cookies.
 */
export function hydrateAuthStorage(): void {
  for (const key of DURABLE_AUTH_KEYS) {
    const fromLs = lsGet(key);
    const fromCookie = Cookies.get(key);
    if (fromLs && !fromCookie) {
      Cookies.set(key, fromLs, getAuthCookieOptions());
    } else if (!fromLs && fromCookie) {
      lsSet(key, fromCookie);
    }
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    const payload = JSON.parse(json) as Record<string, unknown>;
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

function isJwtExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  return payload.exp * 1000 <= Date.now();
}

function jwtMatchesSession(token: string, sessionToken: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.sessionid !== 'string') return false;
  return payload.sessionid === sessionToken;
}

function pruneStaleJwt(): void {
  const token = readDurable(JWT_COOKIE);
  if (!token) return;

  const sessionToken = readDurable(SESSION_COOKIE);
  if (isJwtExpired(token) || (sessionToken && !jwtMatchesSession(token, sessionToken))) {
    removeDurable(JWT_COOKIE);
  }
}

/**
 * Session check URL — JWT-first when a valid c8_jwt exists, otherwise legacy session token.
 * Stale/expired JWTs are pruned before this runs so they never block a good session cookie.
 */
export function getVerifySessionUrl(): string | null {
  pruneStaleJwt();
  const base = process.env.API_URL;
  if (!base) return null;
  const jwt = getJwt();
  if (jwt) return `${base}/verifysession`;
  const session_token = getSessionToken();
  if (session_token) return `${base}/verifysession/${session_token}`;
  return null;
}

/** Clear cookies, localStorage mirror, and client session cache used for auth / profile bootstrap. */
export function clearAllClientAuth(): void {
  removeDurable(SESSION_COOKIE);
  removeDurable(USER_ID_COOKIE);
  removeDurable(JWT_COOKIE);
  try {
    sessionStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // ignore
  }
}

const LOGOUT_FLAG = '__c8LogoutInProgress';

/** True once the user chose to log out on this page; cleared by the reload that follows. */
export function isLogoutInProgress(): boolean {
  return typeof window !== 'undefined' && (window as unknown as Record<string, unknown>)[LOGOUT_FLAG] === true;
}

/**
 * User-initiated logout: clear auth and block any in-flight response (e.g. verifysession's
 * refreshed JWT) from writing tokens back before the page navigates away.
 */
export function beginLogout(): void {
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>)[LOGOUT_FLAG] = true;
  }
  clearAllClientAuth();
}

/** Dispatched after tokens are cleared so React (e.g. UserContext) can drop in-memory user. */
export const ACCOUNT_DELETED_LOGOUT_EVENT = 'c8-auth-force-logout';

/** Call when API returns 401 with error account_deleted (or equivalent). */
export function applyAccountDeletedLogout(): void {
  clearAllClientAuth();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ACCOUNT_DELETED_LOGOUT_EVENT));
  }
}

/** If response is 401 with body.error === account_deleted or login_blocked, clear auth and notify app. */
export async function consumeAccountDeletedIfPresent(response: Response): Promise<boolean> {
  if (response.status !== 401) return false;
  try {
    const data = (await response.clone().json()) as { error?: string };
    if (data?.error === 'account_deleted' || data?.error === 'login_blocked') {
      applyAccountDeletedLogout();
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

/** Get JWT (localStorage-first, cookie fallback) if present and still valid for the current session. */
export function getJwt(): string | undefined {
  pruneStaleJwt();
  return readDurable(JWT_COOKIE);
}

/** Get the session token (localStorage-first, cookie fallback). */
export function getSessionToken(): string | undefined {
  return readDurable(SESSION_COOKIE);
}

/** Get the cached user id (localStorage-first, cookie fallback). */
export function getUserId(): string | undefined {
  return readDurable(USER_ID_COOKIE);
}

/** Store the user id durably (localStorage + cookie). */
export function setUserId(userId: string | number): void {
  const value = String(userId ?? '').trim();
  if (!value || isLogoutInProgress()) return;
  writeDurable(USER_ID_COOKIE, value);
}

/** Headers for API calls: includes Authorization Bearer when JWT exists. */
export function getAuthHeaders(): Record<string, string> {
  const jwt = getJwt();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getDeviceHeaders(),
  };
  if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }
  return headers;
}

/** Remove JWT from durable storage (call on logout or when stale). */
export function clearJwt(): void {
  removeDurable(JWT_COOKIE);
}

/** Store JWT durably (call after login when backend returns data.jwt). Same options as session token. */
export function setJwt(jwt: string, options?: AuthCookieOptions) {
  if (isLogoutInProgress()) return;
  writeDurable(JWT_COOKIE, jwt, options);
}

/** Store session token durably (call after login when backend returns data.token). */
export function setSessionToken(token: string, options?: AuthCookieOptions) {
  if (isLogoutInProgress()) return;
  writeDurable(SESSION_COOKIE, token, options);
}

/**
 * Replace auth tokens atomically: clear legacy/stale values first, then write the new session.
 * Call on every successful login (Google, Facebook, phone OTP).
 */
export function storeAuthTokens(sessionToken: string, jwt?: string): void {
  clearAllClientAuth();
  setSessionToken(sessionToken);
  if (jwt) setJwt(jwt);
}

/** Store a refreshed JWT from an auth API response (e.g. session-path verifysession). */
export function applyRefreshedJwtFromResponse(data: { jwt?: unknown }): void {
  if (typeof data.jwt === 'string' && data.jwt.length > 0 && data.jwt !== getJwt()) {
    setJwt(data.jwt);
  }
}

/** Remove jwt from a verifysession payload before normalizing into User state. */
export function stripJwtFromSessionPayload<T extends Record<string, unknown>>(data: T): Omit<T, 'jwt'> {
  const { jwt: _jwt, ...rest } = data;
  return rest;
}
